import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, AlertCircle, Loader2, Check, RefreshCw, Car } from 'lucide-react';
import { API } from '../App';
import AppTopHeader from '../components/AppTopHeader';

const RenewalPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [qrInfo, setQrInfo] = useState(null);
  const [renewal, setRenewal] = useState({
    renewal_days: 365
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQRInfo = async () => {
      try {
        const response = await axios.get(`${API}/q/${qrId}/info`);
        setQrInfo(response.data);
        setError(null);
      } catch (err) {
        setError('QR code not found');
        setQrInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchQRInfo();
  }, [qrId]);

  const calculateNewExpiry = () => {
    const today = new Date();
    const newDate = new Date(today.getTime() + renewal.renewal_days * 24 * 60 * 60 * 1000);
    return newDate.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/q/${qrId}/renew`,
        { renewal_days: renewal.renewal_days },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success('QR code subscription renewed successfully!');
      setSuccess(true);
      
      // Redirect after success
      setTimeout(() => {
        navigate(`/dashboard`);
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to renew QR code';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg font-medium text-gray-700">Loading QR information...</p>
        </div>
      </div>
    );
  }

  if (!qrInfo && error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid QR Code</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Renewed!</h1>
          <p className="text-gray-600 mb-2">Your subscription has been extended</p>
          <p className="text-lg font-semibold text-green-600 mb-6">
            Until {calculateNewExpiry()}
          </p>
          <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100">
      <AppTopHeader backLabel="Back" />
      <div className="flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">

        <h1 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Barlow' }}>Renew Your QR</h1>

        {/* Alert */}
        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-6 flex gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-orange-900 text-sm">Subscription Expired</p>
            <p className="text-orange-800 text-sm">
              {qrInfo?.expiry_date ? `Expired on ${new Date(qrInfo.expiry_date).toLocaleDateString()}` : 'Your subscription has expired'}
            </p>
          </div>
        </div>

        {/* QR Info */}
        <div className="space-y-3 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-600 mb-1">QR Code ID</p>
            <p className="text-lg font-mono font-bold text-indigo-600">{qrId}</p>
          </div>
          
          {qrInfo?.vehicle_number && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-blue-600" />
                <p className="text-xs text-blue-600">Vehicle Registration</p>
              </div>
              <p className="text-lg font-semibold text-blue-700">{qrInfo.vehicle_number}</p>
            </div>
          )}

          {qrInfo?.renewal_count !== undefined && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-xs text-purple-600 mb-1">Previous Renewals</p>
              <p className="text-lg font-semibold text-purple-700">{qrInfo.renewal_count} time(s)</p>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Renewal Duration */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Renewal Duration
              </div>
            </label>
            <select
              value={renewal.renewal_days}
              onChange={(e) => setRenewal({ renewal_days: parseInt(e.target.value) })}
              className="w-full px-4 py-2 rounded-lg border-2 border-gray-300 focus:border-indigo-500 focus:outline-none transition"
            >
              <option value={30}>1 Month (30 days)</option>
              <option value={90}>3 Months (90 days)</option>
              <option value={180}>6 Months (180 days)</option>
              <option value={365}>1 Year (365 days) - Best Value</option>
            </select>
          </div>

          {/* New Expiry Preview */}
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
            <p className="text-xs text-indigo-600 mb-1">New Expiry Date</p>
            <p className="text-lg font-bold text-indigo-700">{calculateNewExpiry()}</p>
            <p className="text-xs text-indigo-600 mt-2">
              {renewal.renewal_days} days from today
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2 mt-6"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Renew Now
              </>
            )}
          </button>
        </form>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>💡 Tip:</strong> You can renew anytime before or after expiry. Your new expiry date starts from today.
          </p>
        </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
            <p>Need assistance? Contact support@sideo.in</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalPage;
